import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Modal, Checkbox, Button, Tooltip, notification } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { createGlobalStyle } from 'styled-components';
import { exportFacilityTemplateReportByGod } from '../../../Apis/Facility';
import { downloadFile } from '../../../Utils/downloadFile';

const ExportModalGlobalStyle = createGlobalStyle`
  .facility-report-export-modal .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
  }
`;

const REPORT_INFO =
  'Downloads one row per facility imported from a facility template, including deleted templates, with ' +
  'its Facility Template Status (Active/Deleted), imported facility name and clinic admin.';
const INCLUDE_USERS_INFO =
  'Adds the users of each facility: one row per clinic that imported it, showing the facility status in ' +
  'that clinic (Enabled/Disabled), its Clinic Admin, Admins, Doctors, Reviewers, Billers and Scribes. ' +
  'Facilities no clinic has imported still get one row.';
const INCLUDE_INACTIVE_INFO =
  'Also lists users whose accounts are disabled. Without this, only active users and users with a pending ' +
  'invitation are listed. Available when Include Users is ticked.';

// Kept outside the Checkbox label so hovering or clicking it never toggles the box
const InfoTip = ({ title, className }) => (
  <Tooltip title={title} overlayStyle={{ maxWidth: 320 }}>
    <InfoCircleOutlined
      className={className}
      style={{ fontSize: 14, color: '#555', marginLeft: 4, cursor: 'pointer' }}
    />
  </Tooltip>
);

const readBlobText = blob =>
  new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => resolve('');
    reader.readAsText(blob);
  });

// The download is requested as a blob, so the backend's error body arrives as a
// blob too: plain text from the export, or JSON with a `message` (e.g. a 403)
export const getExportErrorMessage = async err => {
  if (!err?.response) return 'Unable to connect to the server';
  const { data } = err.response;
  const text = data instanceof Blob ? await readBlobText(data) : data;
  if (text && typeof text === 'object')
    return text.message || 'Please try again later';
  const body = typeof text === 'string' ? text.trim() : '';
  if (!body) return 'Please try again later';
  try {
    const parsed = JSON.parse(body);
    if (parsed && typeof parsed === 'object')
      return parsed.message || 'Please try again later';
  } catch (e) {
    // plain-text message
  }
  return body;
};

const FacilityReportExportModal = ({ visible, onClose }) => {
  const [includeUsers, setIncludeUsers] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Start every export from a clean form
  useEffect(() => {
    if (visible) {
      setIncludeUsers(false);
      setIncludeInactive(false);
    }
  }, [visible]);

  const onIncludeUsersChange = e => {
    setIncludeUsers(e.target.checked);
    if (!e.target.checked) setIncludeInactive(false);
  };

  const handleExport = () => {
    setExporting(true);
    exportFacilityTemplateReportByGod({ includeUsers, includeInactive })
      .then(response => {
        setExporting(false);
        downloadFile(
          response,
          `Facility_Report_${moment().format('YYYYMMDD')}.csv`
        );
        onClose();
      })
      .catch(async err => {
        console.log(err);
        setExporting(false);
        // An expired token already redirects to login with its own message
        if (err?.response?.status === 401) return;
        notification['error']({
          message: 'Failed',
          description: await getExportErrorMessage(err)
        });
      });
  };

  return (
    <>
      <ExportModalGlobalStyle />
      <Modal
        title='Export Facility Report'
        className='facility-report-export-modal'
        visible={visible}
        style={{ top: 150 }}
        onCancel={exporting ? undefined : onClose}
        maskClosable={!exporting}
        closable={!exporting}
        destroyOnClose
        footer={[
          <Button
            key='cancel'
            onClick={onClose}
            disabled={exporting}
            style={{ borderRadius: 8 }}
          >
            Cancel
          </Button>,
          <Button
            key='export'
            type='primary'
            loading={exporting}
            onClick={handleExport}
            style={{ borderRadius: 8 }}
          >
            Export
          </Button>
        ]}
      >
        <p>
          Exports every facility, including deleted ones, as a CSV file.
          <InfoTip title={REPORT_INFO} className='report-info' />
        </p>
        <div>
          <Checkbox
            className='include-users-checkbox'
            checked={includeUsers}
            disabled={exporting}
            onChange={onIncludeUsersChange}
          >
            Include Users
          </Checkbox>
          <InfoTip title={INCLUDE_USERS_INFO} className='include-users-info' />
        </div>
        <div style={{ marginTop: 8, marginLeft: 24 }}>
          <Checkbox
            className='include-inactive-checkbox'
            checked={includeInactive}
            disabled={!includeUsers || exporting}
            onChange={e => setIncludeInactive(e.target.checked)}
          >
            Include Inactive Users
          </Checkbox>
          <InfoTip
            title={INCLUDE_INACTIVE_INFO}
            className='include-inactive-info'
          />
        </div>
      </Modal>
    </>
  );
};

export default FacilityReportExportModal;
